<template>
  <div>
    <loading
      :active.sync="loading"
      :can-cancel="false"
      background-color="#fff"
      :is-full-page="false"
    />

    <div v-for="(msg, i) in errors" :key="i">
      <CAlert color="danger" closeButton>{{ msg }}</CAlert>
    </div>

    <!-- Encabezado + filtros -->
    <b-row class="mb-3 align-items-end">
      <b-col>
        <h3 class="mb-0">Dashboard Ejecutivo</h3>
        <small class="text-muted">{{ dateLabel }}</small>
      </b-col>
      <b-col lg="4">
        <small>Ciudad</small>
        <v-select
          v-model="citySelected"
          :options="cities"
          multiple
          placeholder="Todas las ciudades"
        />
      </b-col>
      <b-col lg="2">
        <b-button variant="warning" @click="submit" :disabled="loading">
          Consultar
        </b-button>
      </b-col>
    </b-row>

    <div v-if="hasData">

      <!-- ══════════════════════════════════════════════════════
           SECCIÓN 1: PACE DEL MES
      ══════════════════════════════════════════════════════ -->
      <b-row class="mb-3">
        <!-- Velocímetro principal -->
        <b-col lg="4">
          <CCard :class="`border-${paceColor} h-100`" style="border-left: 4px solid;">
            <CCardBody>
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <div class="text-muted small text-uppercase font-weight-bold">Pace del mes</div>
                  <div class="display-4 font-weight-bold mt-1" :class="`text-${paceColor}`">
                    {{ pace.paceVsAvg | signedPercent }}
                  </div>
                  <div class="text-muted small">vs promedio último trimestre</div>
                </div>
                <div class="text-right">
                  <div class="font-weight-bold">Día {{ pace.daysElapsed }}/{{ pace.totalDays }}</div>
                  <div class="text-muted small">{{ pace.pacePercentage }}% del mes</div>
                </div>
              </div>
              <b-progress class="mt-3" height="8px">
                <b-progress-bar :value="pace.pacePercentage" :variant="paceColor" />
              </b-progress>
            </CCardBody>
          </CCard>
        </b-col>

        <!-- Ingresos actuales vs esperado -->
        <b-col lg="2" sm="6">
          <CCard class="h-100">
            <CCardBody>
              <div class="text-muted small text-uppercase">Ingresos a la fecha</div>
              <div class="h4 font-weight-bold mt-1">{{ pace.currentIncome | thousand }}</div>
              <div class="text-muted small">Esperado proporcional:</div>
              <div class="font-weight-bold" :class="pace.currentIncome >= pace.expectedAtThisPoint ? 'text-success' : 'text-danger'">
                {{ pace.expectedAtThisPoint | thousand }}
              </div>
            </CCardBody>
          </CCard>
        </b-col>

        <!-- Proyección mes completo -->
        <b-col lg="2" sm="6">
          <CCard class="h-100">
            <CCardBody>
              <div class="text-muted small text-uppercase">Proyección mes</div>
              <div class="h4 font-weight-bold mt-1" :class="`text-${paceColor}`">
                {{ pace.projectedMonthlyIncome | thousand }}
              </div>
              <div class="text-muted small">Promedio últimos 3 meses:</div>
              <div class="font-weight-bold">{{ pace.avgLastQuarterIncome | thousand }}</div>
            </CCardBody>
          </CCard>
        </b-col>

        <!-- Gastos -->
        <b-col lg="2" sm="6">
          <CCard class="h-100">
            <CCardBody>
              <div class="text-muted small text-uppercase">Gastos a la fecha</div>
              <div class="h4 font-weight-bold mt-1 text-danger">{{ currentExpenses | thousand }}</div>
              <div class="text-muted small">Ratio gastos/ingresos:</div>
              <div class="font-weight-bold" :class="spendingRatio > 0.60 ? 'text-danger' : spendingRatio > 0.45 ? 'text-warning' : 'text-success'">
                {{ (spendingRatio * 100).toFixed(1) }}%
              </div>
            </CCardBody>
          </CCard>
        </b-col>

        <!-- Cartera pendiente -->
        <b-col lg="2" sm="6">
          <CCard class="h-100">
            <CCardBody>
              <div class="text-muted small text-uppercase">Cartera pendiente</div>
              <div class="h4 font-weight-bold mt-1 text-warning">{{ currentPortfolio | thousand }}</div>
              <div class="text-muted small">% de pedidos pendientes:</div>
              <div class="font-weight-bold" :class="portfolioRatio > 0.30 ? 'text-danger' : 'text-muted'">
                {{ (portfolioRatio * 100).toFixed(1) }}%
              </div>
            </CCardBody>
          </CCard>
        </b-col>
      </b-row>

      <!-- ══════════════════════════════════════════════════════
           SECCIÓN 2: ALERTAS ACCIONABLES
      ══════════════════════════════════════════════════════ -->
      <b-row class="mb-3">
        <b-col lg="12">
          <CCard>
            <CCardHeader><strong>Alertas y acciones recomendadas</strong></CCardHeader>
            <CCardBody>
              <b-row>
                <b-col
                  v-for="(alert, i) in alerts"
                  :key="i"
                  lg="4" md="6"
                  class="mb-3"
                >
                  <div :class="['alert-card', `alert-${alert.level}`]" class="p-3 rounded h-100">
                    <div class="font-weight-bold mb-1">{{ alert.icon }} {{ alert.title }}</div>
                    <div class="small text-muted mb-2">{{ alert.body }}</div>
                    <div v-if="alert.action" class="small font-weight-bold" :class="`text-${alert.level === 'danger' ? 'danger' : alert.level === 'warning' ? 'warning' : 'success'}`">
                      → {{ alert.action }}
                    </div>
                  </div>
                </b-col>
              </b-row>
            </CCardBody>
          </CCard>
        </b-col>
      </b-row>

      <!-- ══════════════════════════════════════════════════════
           SECCIÓN 3: DESGLOSE SEMANAL
      ══════════════════════════════════════════════════════ -->
      <b-row class="mb-3">
        <b-col lg="6">
          <CCard>
            <CCardHeader><strong>Ingresos por semana — mes actual vs anterior</strong></CCardHeader>
            <CCardBody>
              <div v-for="week in weeklyBreakdown" :key="week.week" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span class="small font-weight-bold">{{ week.label }}</span>
                  <span class="small">
                    <strong>{{ week.income | thousand }}</strong>
                    <span class="text-muted ml-1">vs {{ week.prevMonthIncome | thousand }}</span>
                    <CBadge
                      v-if="week.change !== null"
                      :color="week.change >= 0 ? 'success' : 'danger'"
                      class="ml-2"
                    >{{ week.change | signedPercent }}</CBadge>
                    <CBadge v-else color="secondary" class="ml-2">Pendiente</CBadge>
                  </span>
                </div>
                <b-progress height="6px">
                  <b-progress-bar
                    :value="week.prevMonthIncome > 0 ? Math.min((week.income / week.prevMonthIncome) * 100, 150) : 0"
                    :variant="week.change !== null && week.change >= 0 ? 'success' : week.income > 0 ? 'danger' : 'secondary'"
                  />
                </b-progress>
              </div>
            </CCardBody>
          </CCard>
        </b-col>

        <!-- Mix de servicios -->
        <b-col lg="6">
          <CCard>
            <CCardHeader><strong>Mix de servicios — mes actual</strong></CCardHeader>
            <CCardBody>
              <div v-if="servicesMix.length === 0" class="text-muted text-center py-3">
                Sin datos de servicios
              </div>
              <div v-for="(service, i) in servicesMix" :key="i" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span class="small font-weight-bold text-truncate" style="max-width:60%">{{ service.name }}</span>
                  <span class="small">
                    {{ service.amount | thousand }}
                    <span class="text-muted">({{ service.percentage }}%)</span>
                  </span>
                </div>
                <b-progress height="6px">
                  <b-progress-bar :value="service.percentage" variant="info" />
                </b-progress>
              </div>
            </CCardBody>
          </CCard>
        </b-col>
      </b-row>

      <!-- ══════════════════════════════════════════════════════
           SECCIÓN 4: TENDENCIA HISTÓRICA
      ══════════════════════════════════════════════════════ -->
      <b-row class="mb-3">
        <b-col lg="8">
          <CCard>
            <CCardHeader><strong>Tendencia últimos meses</strong></CCardHeader>
            <CCardBody>
              <b-table
                :items="historicalTrend"
                :fields="historicalFields"
                small
                responsive
                hover
                show-empty
                empty-text="Sin historial disponible"
              >
                <template #cell(income)="{ value }">
                  <span class="text-success font-weight-bold">{{ value | thousand }}</span>
                </template>
                <template #cell(expenses)="{ value }">
                  <span class="text-danger">{{ value | thousand }}</span>
                </template>
                <template #cell(utility)="{ value }">
                  <span :class="value >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                    {{ value | thousand }}
                  </span>
                </template>
                <template #cell(projected)="{ value }">
                  <span class="text-info">{{ value | thousand }}</span>
                </template>
              </b-table>
            </CCardBody>
          </CCard>
        </b-col>

        <!-- Gastos detalle -->
        <b-col lg="4">
          <CCard>
            <CCardHeader><strong>Gastos por concepto — mes actual</strong></CCardHeader>
            <CCardBody>
              <div v-if="spendingsDetail.length === 0" class="text-muted text-center py-3">
                Sin gastos registrados
              </div>
              <div v-for="(spend, i) in spendingsDetail" :key="i" class="mb-2">
                <div class="d-flex justify-content-between">
                  <span class="small text-truncate" style="max-width:65%">{{ spend.concept }}</span>
                  <span class="small font-weight-bold text-danger">{{ spend.amount | thousand }}</span>
                </div>
                <b-progress height="4px">
                  <b-progress-bar
                    :value="currentExpenses > 0 ? (spend.amount / currentExpenses) * 100 : 0"
                    variant="danger"
                  />
                </b-progress>
              </div>
            </CCardBody>
          </CCard>
        </b-col>
      </b-row>

    </div>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import moment from "moment";
moment.locale("es");

const OWNER_ID = 1;
const ADMIN_ID = 2;

export default {
  name: "ExecutivePaceDashboard",
  components: { Loading },
  filters: {
    thousand(value) {
      if (value == null || isNaN(value)) return "—";
      return Number(value).toLocaleString("de-DE");
    },
    signedPercent(value) {
      if (value == null) return "—";
      const sign = value >= 0 ? "+" : "";
      return `${sign}${Number(value).toFixed(1)}%`;
    },
  },
  data() {
    return {
      loading: true,
      errors: [],
      cities: [{ value: 0, label: "Todas" }],
      citySelected: [{ value: 0, label: "Todas" }],
      hasData: false,
      pace: {},
      weeklyBreakdown: [],
      historicalTrend: [],
      servicesMix: [],
      spendingsDetail: [],
      currentExpenses: 0,
      currentIncome: 0,
      currentPortfolio: 0,
      spendingRatio: 0,
      portfolioRatio: 0,
      alerts: [],
      historicalFields: [
        { key: "month", label: "Mes" },
        { key: "income", label: "Ingresos" },
        { key: "expenses", label: "Gastos" },
        { key: "utility", label: "Utilidad" },
        { key: "projected", label: "Proyectado" },
      ],
    };
  },
  computed: {
    dateLabel() {
      return moment().format("dddd D [de] MMMM YYYY");
    },
    paceColor() {
      return this.pace.paceStatus === "danger"
        ? "danger"
        : this.pace.paceStatus === "warning"
        ? "warning"
        : "success";
    },
  },
  created() {
    this.userProfile = JSON.parse(this.$store.getters.userProfile);
    const access = this.userProfile.acceso.id;
    if (access === OWNER_ID || access === ADMIN_ID) {
      this.fetchCities();
    } else {
      this.errors.push("No posees los permisos para este módulo");
      this.loading = false;
    }
  },
  methods: {
    fetchCities() {
      this.loading = true;
      const getCities = this.$firebase.functions().httpsCallable("getCitiesV2");
      getCities()
        .then((response) => {
          if (response.data != null) {
            const citiesList = response.data.data;
            if (this.userProfile.acceso.id === OWNER_ID) {
              citiesList.forEach((city) => {
                this.cities.push({ value: city.id, label: city.nombre });
              });
            } else if (this.userProfile.acceso.id === ADMIN_ID) {
              const cityFound = citiesList.find((c) => c.nombre === this.userProfile.ciudad);
              this.citySelected = [{ value: cityFound.id, label: cityFound.nombre }];
              this.cities = [{ value: cityFound.id, label: cityFound.nombre }];
            }
          }
        })
        .catch((err) => console.log(err))
        .finally(() => {
          this.loading = false;
          this.submit();
        });
    },
    submit() {
      this.loading = true;
      this.errors = [];

      let cityPayload;
      if (!this.citySelected.length || this.citySelected[0].value === 0) {
        cityPayload = { value: 0 };
      } else {
        cityPayload = this.citySelected.map((c) => ({ value: c.value, label: c.label }));
      }

      const fn = this.$firebase.functions().httpsCallable("executivePaceDashboard");
      fn({ city: cityPayload })
        .then((res) => {
          const d = res.data;
          this.pace = d.pace;
          this.weeklyBreakdown = d.weeklyBreakdown;
          this.historicalTrend = d.historicalTrend;
          this.servicesMix = d.servicesMix;
          this.spendingsDetail = d.spendingsDetail;
          this.currentExpenses = d.currentExpenses;
          this.currentIncome = d.currentIncome;
          this.currentPortfolio = d.currentPortfolio;
          this.spendingRatio = d.spendingRatio;
          this.portfolioRatio = d.portfolioRatio;
          this.alerts = d.alerts;
          this.hasData = true;
        })
        .catch((err) => {
          console.log(err);
          this.errors.push("Error al cargar el dashboard ejecutivo");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.alert-card {
  border-left: 4px solid;
}
.alert-danger {
  background: #fff5f5;
  border-color: #dc3545;
}
.alert-warning {
  background: #fffbf0;
  border-color: #ffc107;
}
.alert-success {
  background: #f0fff4;
  border-color: #28a745;
}
.alert-info {
  background: #f0f8ff;
  border-color: #17a2b8;
}
</style>
